import {
  Alert,
  AnimatedContentLoader,
  Badge,
  Button,
  Input,
  Prompt,
  Select,
  SingleColumnLayout,
  TagPill
} from '@components'
import { InputGroup } from '@components/Inputs/InputGroup.tsx'
import {
  getExpenseClaimExpensePageUrl,
  getExpenseClaimPageUrl,
  getExpenseClaimTripPageUrl,
  getTravelAndExpensesPageUrl
} from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import {
  ExpenseClaimLeg,
  ExpenseClaimLegWrite,
  ExpenseClaimTrip,
  createExpenseClaimLeg,
  createExpenseClaimTrip,
  deleteExpenseClaimLeg,
  expenseClaimFieldError,
  fetchExpenseClaim,
  fetchExpenseClaimExpenses,
  fetchExpenseClaimRates,
  fetchExpenseClaimTrip,
  fetchPerDiemDestinations,
  recalculateExpenseClaim,
  updateExpenseClaimExpense,
  updateExpenseClaimLeg,
  updateExpenseClaimTrip,
  useCurrentUser
} from '@query'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'
import { formatDateTime } from '@utils'
import { formatEurosWithOpts } from '@utils/formatCurrency'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaCalendarAlt,
  FaCar,
  FaChevronRight,
  FaFileInvoice,
  FaMapMarkerAlt,
  FaPlus,
  FaRegCircle,
  FaSave
} from 'react-icons/fa'
import { Link, useHistory, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { LegDraft, LegDrawer, parseKilometres, vehicleLabel } from './LegDrawer'
import { PerDiemSection } from './PerDiemSection'
import { TripSection } from './TripSection'
import {
  AmountLines,
  Aside,
  AsideActionFullWidth,
  AsideFormActions,
  Card,
  CardTitle,
  Columns,
  Help,
  MainColumn,
  PageContent,
  PageScroll,
  PageSubtitle,
  PageTitle,
  PageTop,
  PageTopMain,
  STATUS_BADGE,
  TextLink,
  TotalLine,
  buildExpenseListMeta,
  claimKey,
  expenseClaimExpensesKey,
  expenseDisplayTitle
} from './shared'

const tripKey = (slug: string, claimId: number, tripId: number) => [
  slug,
  'expense-claim-trip',
  claimId,
  tripId
]

const toLocalInput = (iso: string) => {
  const date = dayjs(iso)
  return date.isValid() ? date.format('YYYY-MM-DDTHH:mm') : ''
}

const fromLocalInput = (local: string) => new Date(local).toISOString()

const blankLeg = (partial: Partial<LegDraft>): LegDraft => ({
  departure_address: '',
  destination_address: '',
  description: '',
  departure_at: '',
  arrival_at: '',
  vehicle: 'CAR',
  distance_km: '',
  route_distance_km: null,
  distance_source: 'MANUAL',
  departure_place_id: '',
  destination_place_id: '',
  extra_passengers: '0',
  has_trailer: false,
  has_heavy_load: false,
  country_change: '',
  ...partial
})

const draftFromLeg = (leg: ExpenseClaimLeg): LegDraft =>
  blankLeg({
    legId: leg.id,
    departure_address: leg.departure_address,
    destination_address: leg.destination_address,
    description: leg.description,
    departure_at: toLocalInput(leg.departure_at),
    arrival_at: toLocalInput(leg.arrival_at),
    vehicle: leg.vehicle,
    distance_km: leg.distance_km.replace('.', ','),
    route_distance_km: leg.route_distance_km,
    distance_source: leg.distance_source,
    extra_passengers: String(leg.extra_passengers),
    has_trailer: leg.has_trailer,
    has_heavy_load: leg.has_heavy_load,
    country_change: leg.country_change ?? ''
  })

const legPayload = (draft: LegDraft): ExpenseClaimLegWrite | null => {
  const kilometres = parseKilometres(draft.distance_km)
  if (kilometres === null) return null
  const passengers = Number(draft.extra_passengers)
  return {
    departure_address: draft.departure_address,
    destination_address: draft.destination_address,
    description: draft.description,
    departure_at: fromLocalInput(draft.departure_at),
    arrival_at: fromLocalInput(draft.arrival_at),
    vehicle: draft.vehicle,
    distance_km: kilometres.toFixed(1),
    route_distance_km: draft.route_distance_km,
    distance_source: draft.distance_source,
    extra_passengers: Number.isFinite(passengers) ? Math.max(0, Math.trunc(passengers)) : 0,
    has_trailer: draft.vehicle === 'COMPANY_CAR' ? false : draft.has_trailer,
    has_heavy_load: draft.has_heavy_load,
    country_change: draft.country_change || null,
    ...(draft.position ? { position: draft.position } : {})
  }
}

export const TripEditorPage: React.FC = () => {
  const [t] = useTranslation()
  const history = useHistory()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const { claimId: claimIdParam, tripId: tripIdParam } = useParams<{
    claimId: string
    tripId?: string
  }>()
  const claimId = Number(claimIdParam)
  const tripId = tripIdParam ? Number(tripIdParam) : null
  const isNew = tripId === null
  const canEdit = usePermission(PermissionSet.EXPENSE_CLAIMS_EDIT)
  const { data: currentUser } = useCurrentUser()

  const claimQuery = useQuery({
    queryKey: claimKey(slug, claimId),
    queryFn: () => fetchExpenseClaim(slug, claimId),
    enabled: Number.isInteger(claimId)
  })
  const tripQuery = useQuery({
    queryKey: tripKey(slug, claimId, tripId ?? 0),
    queryFn: () => fetchExpenseClaimTrip(slug, claimId, tripId as number),
    enabled: Number.isInteger(claimId) && tripId !== null
  })
  const { data: claimExpenses = [] } = useQuery({
    queryKey: expenseClaimExpensesKey(slug, claimId),
    queryFn: () => fetchExpenseClaimExpenses(slug, claimId),
    enabled: Number.isInteger(claimId) && tripId !== null
  })
  const expensesById = useMemo(
    () => new Map(claimExpenses.map(expense => [expense.id, expense])),
    [claimExpenses]
  )
  const ratesQuery = useQuery({
    queryKey: [slug, 'expense-claim-rates'],
    queryFn: () => fetchExpenseClaimRates(slug)
  })

  const claim = claimQuery.data
  const trip = tripQuery.data
  const isOwnDraft =
    !!claim && claim.status === 'DRAFT' && claim.claimant.id === currentUser?.id && canEdit
  const recalculatedDraftId = useRef<number | null>(null)

  const recalculateDraft = useMutation({
    mutationFn: () => recalculateExpenseClaim(slug, claimId),
    onSuccess: next => {
      queryClient.setQueryData(claimKey(slug, claimId), next)
      queryClient.invalidateQueries({ queryKey: [slug, 'expense-claim-trips', claimId] })
      if (tripId !== null) {
        queryClient.invalidateQueries({ queryKey: tripKey(slug, claimId, tripId) })
      }
      queryClient.invalidateQueries({ queryKey: expenseClaimExpensesKey(slug, claimId) })
    }
  })

  const [description, setDescription] = useState('')
  const [startAt, setStartAt] = useState('')
  const [endAt, setEndAt] = useState('')
  const [destination, setDestination] = useState('')
  const [autoPerDiem, setAutoPerDiem] = useState(false)
  const [openedMileage, setOpenedMileage] = useState(false)
  const [openedPerDiem, setOpenedPerDiem] = useState(false)
  const [openedReceipts, setOpenedReceipts] = useState(false)
  const [removeMileagePrompt, setRemoveMileagePrompt] = useState(false)
  const [removePerDiemPrompt, setRemovePerDiemPrompt] = useState(false)
  const [removeReceiptsPrompt, setRemoveReceiptsPrompt] = useState(false)
  const [tagIds, setTagIds] = useState<number[]>([])
  const [draft, setDraft] = useState<LegDraft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [legError, setLegError] = useState<string | null>(null)
  const [descriptionError, setDescriptionError] = useState<string | null>(null)
  const [destinationError, setDestinationError] = useState<string | null>(null)
  const hydratedTripId = useRef<number | null>(null)
  const appliedNewTripDefaults = useRef(false)

  useEffect(() => {
    hydratedTripId.current = null
    appliedNewTripDefaults.current = false
  }, [tripId])

  useEffect(() => {
    if (!claim || !isOwnDraft) return
    if (recalculatedDraftId.current === claim.id) return
    recalculatedDraftId.current = claim.id
    recalculateDraft.mutate()
  }, [claim?.id, isOwnDraft])

  useEffect(() => {
    if (trip) {
      if (hydratedTripId.current === trip.id) return
      hydratedTripId.current = trip.id
      setDescription(trip.description)
      setStartAt(toLocalInput(trip.start_at))
      setEndAt(toLocalInput(trip.end_at))
      setDestination(trip.destination)
      setAutoPerDiem(trip.auto_per_diem)
      setTagIds(trip.tags.map(tag => tag.id))
      return
    }
    if (isNew && !appliedNewTripDefaults.current) {
      appliedNewTripDefaults.current = true
      const now = dayjs().format('YYYY-MM-DDTHH:mm')
      setStartAt(now)
      setEndAt(now)
    }
  }, [trip, isNew])

  useEffect(() => {
    setOpenedMileage(false)
    setOpenedPerDiem(false)
    setOpenedReceipts(false)
  }, [tripId])

  const onDate = startAt.slice(0, 10)
  const destinationsQuery = useQuery({
    queryKey: [slug, 'per-diem-destinations', onDate],
    queryFn: () => fetchPerDiemDestinations(slug, onDate),
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(onDate)
  })
  const destinations = destinationsQuery.data?.results ?? []

  useEffect(() => {
    if (!isNew || destination || !destinationsQuery.data) return
    setDestination(destinationsQuery.data.domestic_key)
  }, [isNew, destination, destinationsQuery.data])

  const messageFrom = (saveError: unknown) =>
    [
      'detail',
      'description',
      'start_at',
      'end_at',
      'destination',
      'tag_ids',
      'departure_address',
      'destination_address',
      'departure_at',
      'arrival_at',
      'has_trailer',
      'distance_km',
      'vehicle',
      'country_change'
    ]
      .map(field => expenseClaimFieldError(saveError, field))
      .find((value): value is string => Boolean(value)) ?? t('travelAndExpenses.loadError')

  const refresh = (saved?: ExpenseClaimTrip) => {
    queryClient.invalidateQueries({ queryKey: claimKey(slug, claimId) })
    queryClient.invalidateQueries({ queryKey: [slug, 'expense-claim-trips', claimId] })
    queryClient.invalidateQueries({ queryKey: expenseClaimExpensesKey(slug, claimId) })
    queryClient.invalidateQueries({ queryKey: [slug, 'expense-claims'] })
    const id = saved?.id ?? tripId
    if (id) {
      if (saved) queryClient.setQueryData(tripKey(slug, claimId, id), saved)
      queryClient.invalidateQueries({ queryKey: tripKey(slug, claimId, id) })
    }
  }

  const openNewLeg = () => {
    const arrivalAt =
      endAt && new Date(endAt).getTime() >= new Date(startAt).getTime() ? endAt : startAt
    setLegError(null)
    setDraft(
      blankLeg({
        departure_address: claim?.claimant_profile?.home_address ?? '',
        departure_at: startAt,
        arrival_at: arrivalAt,
        vehicle: 'CAR'
      })
    )
  }

  const tripWrite = (autoPerDiemValue: boolean) => ({
    description,
    start_at: fromLocalInput(startAt),
    end_at: fromLocalInput(endAt),
    destination,
    auto_per_diem: autoPerDiemValue,
    tag_ids: tagIds
  })

  const saveTrip = useMutation<
    ExpenseClaimTrip,
    unknown,
    'stay' | 'summary' | 'addLeg' | 'openPerDiem' | 'openReceipts'
  >({
    mutationFn: intent => {
      const payload = tripWrite(intent === 'openPerDiem' ? true : autoPerDiem)
      return tripId === null
        ? createExpenseClaimTrip(slug, claimId, payload)
        : updateExpenseClaimTrip(slug, claimId, tripId, payload)
    },
    onSuccess: (saved, intent) => {
      refresh(saved)
      setError(null)
      if (intent === 'addLeg') openNewLeg()
      if (intent === 'openPerDiem') {
        setOpenedPerDiem(true)
        setAutoPerDiem(true)
        if (saved.id && !saved.auto_per_diem) {
          saveAutomatic.mutate(tripWrite(true))
        }
      }
      if (intent === 'openReceipts') {
        setOpenedReceipts(true)
        history.push(
          getExpenseClaimExpensePageUrl(slug, claimId, 'new', {
            trip: saved.id
          })
        )
      }
      if (intent === 'summary') {
        history.push(getExpenseClaimPageUrl(slug, claimId))
      } else if (isNew && intent !== 'openReceipts') {
        history.replace(getExpenseClaimTripPageUrl(slug, claimId, saved.id))
      }
    },
    onError: saveError => setError(messageFrom(saveError))
  })

  const saveAutomatic = useMutation({
    mutationFn: (payload: ReturnType<typeof tripWrite>) =>
      updateExpenseClaimTrip(slug, claimId, tripId as number, payload),
    onSuccess: saved => {
      refresh(saved)
      setError(null)
    },
    onError: saveError => setError(messageFrom(saveError))
  })

  const saveLeg = useMutation({
    mutationFn: async (next: LegDraft) => {
      if (tripId === null) return
      const payload = legPayload(next)
      if (!payload) return
      if (next.legId) {
        await updateExpenseClaimLeg(slug, claimId, tripId, next.legId, payload)
      } else {
        await createExpenseClaimLeg(slug, claimId, tripId, payload)
      }
      if (next.followingLegId) {
        await updateExpenseClaimLeg(slug, claimId, tripId, next.followingLegId, {
          departure_address: next.destination_address
        })
      }
    },
    onSuccess: () => {
      refresh()
      setDraft(null)
      setLegError(null)
    },
    onError: saveError => setLegError(messageFrom(saveError))
  })

  const removeLeg = useMutation({
    mutationFn: async (legId: number) => {
      if (tripId === null) return
      await deleteExpenseClaimLeg(slug, claimId, tripId, legId)
    },
    onSuccess: () => {
      refresh()
      setDraft(null)
      setLegError(null)
    },
    onError: saveError => setLegError(messageFrom(saveError))
  })

  const removeAllLegs = useMutation({
    mutationFn: async () => {
      if (tripId === null || !trip) return
      for (const leg of trip.legs) {
        await deleteExpenseClaimLeg(slug, claimId, tripId, leg.id)
      }
    },
    onSuccess: () => {
      refresh()
      setDraft(null)
      setOpenedMileage(false)
      setRemoveMileagePrompt(false)
    },
    onError: saveError => setError(messageFrom(saveError))
  })

  const unlinkAllReceipts = useMutation({
    mutationFn: async () => {
      if (tripId === null || !trip) return
      for (const expense of trip.expenses) {
        await updateExpenseClaimExpense(slug, claimId, expense.id, { trip_id: null })
      }
    },
    onSuccess: () => {
      refresh()
      setOpenedReceipts(false)
      setRemoveReceiptsPrompt(false)
    },
    onError: saveError => setError(messageFrom(saveError))
  })

  const copyReturn = useMutation({
    mutationFn: async () => {
      if (!trip || trip.legs.length === 0 || tripId === null) return
      const first = trip.legs[0]
      const last = trip.legs[trip.legs.length - 1]
      const lastArrival = new Date(last.arrival_at).getTime()
      const tripEnd = new Date(trip.end_at).getTime()
      await createExpenseClaimLeg(slug, claimId, tripId, {
        departure_address: last.destination_address,
        destination_address: first.departure_address,
        departure_at: last.arrival_at,
        arrival_at: new Date(Math.max(tripEnd, lastArrival)).toISOString(),
        description: last.description,
        vehicle: last.vehicle,
        distance_km: last.distance_km,
        route_distance_km: last.route_distance_km,
        distance_source: last.distance_source,
        extra_passengers: last.extra_passengers,
        has_trailer: last.has_trailer,
        has_heavy_load: last.has_heavy_load
      })
    },
    onSuccess: () => {
      refresh()
      setError(null)
    },
    onError: saveError => setError(messageFrom(saveError))
  })

  const openAppend = () => {
    if (!trip) return
    const last = trip.legs[trip.legs.length - 1]
    const departureAt = last ? last.arrival_at : trip.start_at
    const arrivalAt =
      new Date(trip.end_at).getTime() >= new Date(departureAt).getTime() ? trip.end_at : departureAt
    setLegError(null)
    setDraft(
      blankLeg({
        departure_address: last
          ? last.destination_address
          : claim?.claimant_profile?.home_address ?? '',
        departure_at: toLocalInput(departureAt),
        arrival_at: toLocalInput(arrivalAt),
        vehicle: last?.vehicle ?? 'CAR',
        extra_passengers: String(last?.extra_passengers ?? 0),
        has_trailer: last?.has_trailer ?? false,
        has_heavy_load: last?.has_heavy_load ?? false
      })
    )
  }

  const openStopBeforeReturn = () => {
    if (!trip || trip.legs.length < 2) return
    const previous = trip.legs[trip.legs.length - 2]
    const returning = trip.legs[trip.legs.length - 1]
    setLegError(null)
    setDraft(
      blankLeg({
        position: returning.position,
        followingLegId: returning.id,
        departure_address: returning.departure_address,
        departure_at: toLocalInput(previous.arrival_at),
        arrival_at: toLocalInput(returning.departure_at),
        vehicle: previous.vehicle,
        extra_passengers: String(previous.extra_passengers),
        has_trailer: previous.has_trailer,
        has_heavy_load: previous.has_heavy_load
      })
    )
  }

  const endsBeforeStart =
    startAt !== '' && endAt !== '' && new Date(endAt).getTime() < new Date(startAt).getTime()
  const legs = trip?.legs ?? []
  const firstLeg = legs[0]
  const lastLeg = legs[legs.length - 1]
  const canInsertStop =
    legs.length >= 2 && !!firstLeg && lastLeg?.destination_address === firstLeg.departure_address
  const mileage = trip?.mileage_amount ?? 0
  const perDiem = trip?.per_diem_amount ?? 0
  const expenseAmount = trip?.expense_amount ?? 0
  const distance = (trip?.distance_km ?? '0.0').replace('.', ',')
  const destinationKnown = destinations.some(row => row.key === destination)
  const canSave =
    isOwnDraft && !endsBeforeStart && description.trim() !== '' && destination.trim() !== ''

  const travelDays = trip?.travel_days ?? []
  const tripExpenses = trip?.expenses ?? []
  const hasMileageData = legs.length > 0
  const hasPerDiemData =
    autoPerDiem || travelDays.some(day => day.manual_amount != null || day.meal_allowance)
  const hasReceiptsData = tripExpenses.length > 0
  const mileageExpanded = hasMileageData || openedMileage
  const perDiemExpanded = hasPerDiemData || openedPerDiem
  const receiptsExpanded = hasReceiptsData || openedReceipts

  const requireTripDetails = (): boolean => {
    if (canSave) return true
    const required = t('validation.required')
    if (!description.trim()) setDescriptionError(required)
    if (!destination.trim()) setDestinationError(required)
    return false
  }

  const requestAddMileage = () => {
    setOpenedMileage(true)
    if (trip) {
      if (canInsertStop) openStopBeforeReturn()
      else openAppend()
      return
    }
    if (!requireTripDetails()) return
    saveTrip.mutate('addLeg')
  }

  const requestAddPerDiem = () => {
    if (endsBeforeStart) return
    setOpenedPerDiem(true)
    setAutoPerDiem(true)
    if (tripId !== null) {
      saveAutomatic.mutate(tripWrite(true))
      return
    }
    if (!requireTripDetails()) return
    saveTrip.mutate('openPerDiem')
  }

  const requestAddReceipts = () => {
    if (tripId !== null) {
      setOpenedReceipts(true)
      history.push(
        getExpenseClaimExpensePageUrl(slug, claimId, 'new', {
          trip: tripId
        })
      )
      return
    }
    if (!requireTripDetails()) return
    saveTrip.mutate('openReceipts')
  }

  const handleRemoveMileage = () => {
    if (legs.length === 0) {
      setOpenedMileage(false)
      return
    }
    setRemoveMileagePrompt(true)
  }

  const applyRemovePerDiem = () => {
    if (endsBeforeStart) return
    setAutoPerDiem(false)
    if (tripId !== null) {
      saveAutomatic.mutate(tripWrite(false), {
        onSuccess: () => {
          setRemovePerDiemPrompt(false)
          setOpenedPerDiem(false)
        }
      })
      return
    }
    setOpenedPerDiem(false)
    setRemovePerDiemPrompt(false)
  }

  const handleRemovePerDiem = () => {
    if (endsBeforeStart) return
    setRemovePerDiemPrompt(true)
  }

  const handleRemoveReceipts = () => {
    if (!hasReceiptsData) {
      setOpenedReceipts(false)
      return
    }
    setRemoveReceiptsPrompt(true)
  }

  const requestAddLeg = () => {
    if (trip) {
      if (canInsertStop) openStopBeforeReturn()
      else openAppend()
      return
    }
    if (!canSave) {
      const required = t('validation.required')
      if (!description.trim()) {
        setDescriptionError(required)
      }
      if (!destination.trim()) {
        setDestinationError(required)
      }
      return
    }
    saveTrip.mutate('addLeg')
  }

  const addLegNode = (
    <TimelineItem>
      <TimelineIcon $dashed>
        <FaPlus />
      </TimelineIcon>
      <AddLegRow>
        <Button
          type="button"
          size="sm"
          isSecondary
          intent="primary"
          icon={<FaPlus />}
          showSpinner={saveTrip.isPending && saveTrip.variables === 'addLeg'}
          onClick={requestAddLeg}
        >
          {t('travelAndExpenses.trip.addLeg')}
        </Button>
        <Help>
          {canInsertStop
            ? t('travelAndExpenses.trip.addStop')
            : legs.length === 0 && t('travelAndExpenses.trip.emptyLegs')}
        </Help>
      </AddLegRow>
    </TimelineItem>
  )

  return (
    <SingleColumnLayout
      header={t('travelAndExpenses.title')}
      headerTo={getTravelAndExpensesPageUrl(slug)}
    >
      <PageScroll>
        <AnimatedContentLoader isLoading={claimQuery.isLoading || tripQuery.isLoading}>
          {(claimQuery.isError || tripQuery.isError) && (
            <Alert type="error" title={t('travelAndExpenses.loadError')} />
          )}
          {claim && (isNew || trip) && (
            <PageContent>
              <PageTop>
                <PageTopMain>
                  <PageTitle>
                    {isNew ? t('travelAndExpenses.trip.titleNew') : trip?.description}
                  </PageTitle>
                  <PageSubtitle>
                    <TextLink to={getExpenseClaimPageUrl(slug, claimId)}>
                      {t('travelAndExpenses.trip.claimLine', { number: claim.number })}
                    </TextLink>
                  </PageSubtitle>
                </PageTopMain>
                <Badge type={STATUS_BADGE[claim.status]}>
                  {t(`travelAndExpenses.status.${claim.status}`)}
                </Badge>
              </PageTop>

              {error && <Alert type="error" title={error} />}

              <Columns>
                <MainColumn>
                  <Card>
                    <CardTitle>{t('travelAndExpenses.trip.details')}</CardTitle>
                    <Input
                      name="description"
                      label={t('travelAndExpenses.trip.description')}
                      required
                      value={description}
                      disabled={!isOwnDraft}
                      errors={descriptionError ? { message: descriptionError } : undefined}
                      onChange={event => {
                        setDescription(event.target.value)
                        setDescriptionError(null)
                      }}
                    />
                    <InputGroup>
                      <Input
                        name="start_at"
                        type="datetime-local"
                        label={t('travelAndExpenses.trip.starts')}
                        required
                        value={startAt}
                        disabled={!isOwnDraft}
                        onChange={event => setStartAt(event.target.value)}
                      />
                      <Input
                        name="end_at"
                        type="datetime-local"
                        label={t('travelAndExpenses.trip.ends')}
                        required
                        value={endAt}
                        disabled={!isOwnDraft}
                        errors={
                          endsBeforeStart
                            ? { message: t('travelAndExpenses.trip.endBeforeStart') }
                            : undefined
                        }
                        onChange={event => setEndAt(event.target.value)}
                      />
                    </InputGroup>
                    <Help>{t('travelAndExpenses.trip.periodHelp')}</Help>
                    <div>
                      <TagSelector
                        label={t('travelAndExpenses.trip.tags')}
                        value={tagIds}
                        isReadOnly={!isOwnDraft}
                        onChange={setTagIds}
                      />
                      <Help>{t('travelAndExpenses.trip.tagsHelp')}</Help>
                    </div>
                    <Select
                      name="destination"
                      label={t('travelAndExpenses.trip.destination')}
                      required
                      value={destination}
                      disabled={!isOwnDraft || destinationsQuery.isLoading}
                      errors={destinationError ? { message: destinationError } : undefined}
                      onChange={event => {
                        setDestination(event.target.value)
                        setDestinationError(null)
                      }}
                    >
                      {destination && !destinationKnown && (
                        <option value={destination}>{destination}</option>
                      )}
                      {destinations.map(row => (
                        <option key={row.key} value={row.key}>
                          {row.label}
                        </option>
                      ))}
                    </Select>
                  </Card>

                  <TripSection
                    icon={<FaCar />}
                    collapsedTitle={t('travelAndExpenses.trip.sectionKilometres')}
                    expandedTitle={t('travelAndExpenses.trip.mileageTitle')}
                    subtitle={t('travelAndExpenses.trip.sectionKilometresSubtitle')}
                    amount={trip ? formatEurosWithOpts(mileage) : undefined}
                    expanded={mileageExpanded}
                    canEdit={isOwnDraft}
                    onAdd={requestAddMileage}
                    onRemove={handleRemoveMileage}
                    showRemove={isOwnDraft && mileageExpanded}
                    addPending={saveTrip.isPending && saveTrip.variables === 'addLeg'}
                  >
                    <Timeline>
                      {firstLeg && (
                        <TimelineItem>
                          <TimelineIcon>
                            <FaRegCircle />
                          </TimelineIcon>
                          <div>
                            <Help>{formatDateTime(firstLeg.departure_at)}</Help>
                            <Address>{firstLeg.departure_address}</Address>
                          </div>
                        </TimelineItem>
                      )}
                      {legs.map((leg, index) => (
                        <React.Fragment key={leg.id}>
                          {isOwnDraft && canInsertStop && index === legs.length - 1 && addLegNode}
                          <TimelineButton
                            type="button"
                            onClick={() => {
                              setLegError(null)
                              setDraft(draftFromLeg(leg))
                            }}
                          >
                            <TimelineIcon>
                              <FaMapMarkerAlt />
                            </TimelineIcon>
                            <LegBody>
                              <Help>{formatDateTime(leg.arrival_at)}</Help>
                              <Address>{leg.destination_address}</Address>
                              <Chips>
                                <Chip>{vehicleLabel(t, leg.vehicle)}</Chip>
                                {leg.extra_passengers > 0 && (
                                  <Chip>
                                    {t('travelAndExpenses.trip.passengers', {
                                      count: leg.extra_passengers
                                    })}
                                  </Chip>
                                )}
                                <Chip>
                                  {leg.distance_km.replace('.', ',')} km
                                  {leg.distance_source === 'ROUTE'
                                    ? ` · ${t('travelAndExpenses.summary.calculatedFromRoute')}`
                                    : leg.route_distance_km
                                    ? ` · ${t('travelAndExpenses.summary.editedManually')}`
                                    : ` · ${t('travelAndExpenses.trip.enteredManually')}`}
                                </Chip>
                                <Chip $highlight>
                                  {`${leg.rate_per_km.replace(
                                    '.',
                                    ','
                                  )} × ${leg.distance_km.replace(
                                    '.',
                                    ','
                                  )} km = ${formatEurosWithOpts(leg.amount)}`}
                                </Chip>
                              </Chips>
                            </LegBody>
                            <FaChevronRight className="chevron" />
                          </TimelineButton>
                        </React.Fragment>
                      ))}
                      {isOwnDraft && !canInsertStop && addLegNode}
                    </Timeline>
                    {isOwnDraft && legs.length > 0 && !canInsertStop && (
                      <Button
                        type="button"
                        intent="primary"
                        isSecondary
                        showSpinner={copyReturn.isPending}
                        onClick={() => copyReturn.mutate()}
                      >
                        {t('travelAndExpenses.trip.copyReturn')}
                      </Button>
                    )}
                    {trip && (
                      <TotalsBox>
                        <AmountLines>
                          <span>{t('travelAndExpenses.trip.distance')}</span>
                          <span>{distance} km</span>
                          <strong>{t('travelAndExpenses.trip.mileage')}</strong>
                          <strong>{formatEurosWithOpts(mileage)}</strong>
                        </AmountLines>
                      </TotalsBox>
                    )}
                  </TripSection>

                  <TripSection
                    icon={<FaFileInvoice />}
                    collapsedTitle={t('travelAndExpenses.trip.receipts')}
                    expandedTitle={t('travelAndExpenses.trip.receipts')}
                    subtitle={t('travelAndExpenses.trip.sectionReceiptsSubtitle')}
                    amount={trip ? formatEurosWithOpts(expenseAmount) : undefined}
                    expanded={receiptsExpanded}
                    canEdit={isOwnDraft}
                    onAdd={requestAddReceipts}
                    onRemove={handleRemoveReceipts}
                    showRemove={isOwnDraft && receiptsExpanded}
                    addPending={saveTrip.isPending && saveTrip.variables === 'openReceipts'}
                  >
                    {isOwnDraft && (
                      <Button
                        type="button"
                        intent="primary"
                        isSecondary
                        onClick={requestAddReceipts}
                      >
                        {t('travelAndExpenses.trip.addReceipt')}
                      </Button>
                    )}
                    {(trip?.expenses ?? []).length === 0 && (
                      <Help>{t('travelAndExpenses.summary.noExpenses')}</Help>
                    )}
                    {(trip?.expenses ?? []).map(slim => {
                      const expense = expensesById.get(slim.id)
                      const title = expenseDisplayTitle(expense?.merchant ?? slim.merchant, t)
                      const meta = expense ? buildExpenseListMeta(expense, t) : null
                      const expenseTags =
                        expense && expense.tags.length > 0 ? expense.tags : claim?.tags ?? []
                      return (
                        <ReceiptLink
                          key={slim.id}
                          to={getExpenseClaimExpensePageUrl(slug, claimId, slim.id)}
                        >
                          <ReceiptMain>
                            <ReceiptHead>
                              <div>
                                <ReceiptTitle>{title}</ReceiptTitle>
                                {meta && <Help>{meta}</Help>}
                                {expense && expenseTags.length > 0 && (
                                  <ReceiptTagRow>
                                    {expenseTags.map(tag => (
                                      <TagPill
                                        key={tag.id}
                                        tag={{
                                          id: tag.id,
                                          name: tag.name ?? '',
                                          color: tag.color ?? undefined
                                        }}
                                      />
                                    ))}
                                    {expense.tags.length === 0 && (claim?.tags.length ?? 0) > 0 && (
                                      <Help>{t('travelAndExpenses.summary.fromClaim')}</Help>
                                    )}
                                  </ReceiptTagRow>
                                )}
                              </div>
                              <ReceiptAmount>
                                {formatEurosWithOpts(expense?.amount ?? slim.amount ?? 0)}
                              </ReceiptAmount>
                            </ReceiptHead>
                          </ReceiptMain>
                        </ReceiptLink>
                      )
                    })}
                  </TripSection>

                  <TripSection
                    icon={<FaCalendarAlt />}
                    collapsedTitle={t('travelAndExpenses.trip.sectionDailyAllowance')}
                    expandedTitle={t('travelAndExpenses.trip.sectionDailyAllowanceExpanded')}
                    subtitle={t('travelAndExpenses.trip.sectionDailyAllowanceSubtitle')}
                    amount={trip ? formatEurosWithOpts(perDiem) : undefined}
                    expanded={perDiemExpanded}
                    canEdit={isOwnDraft}
                    onAdd={requestAddPerDiem}
                    onRemove={handleRemovePerDiem}
                    showRemove={isOwnDraft && perDiemExpanded}
                    addPending={
                      saveTrip.isPending &&
                      (saveTrip.variables === 'openPerDiem' || saveAutomatic.isPending)
                    }
                  >
                    <PerDiemSection
                      slug={slug}
                      claimId={claimId}
                      trip={trip}
                      destinations={destinations}
                      canEdit={isOwnDraft}
                      autoPerDiem={autoPerDiem}
                      onAutoChange={next => {
                        if (endsBeforeStart) return
                        setAutoPerDiem(next)
                        if (tripId !== null) saveAutomatic.mutate(tripWrite(next))
                      }}
                      onChanged={() => refresh()}
                      onError={setError}
                    />
                  </TripSection>
                </MainColumn>

                <Aside>
                  <CardTitle>{t('travelAndExpenses.trip.total')}</CardTitle>
                  <AmountLines>
                    {mileageExpanded && (
                      <>
                        <span>{t('travelAndExpenses.trip.mileageLine', { km: distance })}</span>
                        <span>{formatEurosWithOpts(mileage)}</span>
                      </>
                    )}
                    {receiptsExpanded && (
                      <>
                        <span>{t('travelAndExpenses.trip.expenseLine')}</span>
                        <span>{formatEurosWithOpts(expenseAmount)}</span>
                      </>
                    )}
                    {perDiemExpanded && (
                      <>
                        <span>{t('travelAndExpenses.trip.perDiemLine')}</span>
                        <span>{formatEurosWithOpts(perDiem)}</span>
                      </>
                    )}
                  </AmountLines>
                  <TotalLine>
                    <span>{t('travelAndExpenses.trip.totalLabel')}</span>
                    <strong>{formatEurosWithOpts(mileage + perDiem + expenseAmount)}</strong>
                  </TotalLine>
                  <Help>{t('travelAndExpenses.trip.taxFreeHelp')}</Help>
                  {isOwnDraft && (
                    <AsideFormActions>
                      <Button
                        type="button"
                        intent="success"
                        icon={<FaSave />}
                        showSpinner={saveTrip.isPending && saveTrip.variables === 'stay'}
                        disabled={!canSave}
                        onClick={() => saveTrip.mutate('stay')}
                      >
                        {t('travelAndExpenses.trip.save')}
                      </Button>
                      <AsideActionFullWidth>
                        <Button
                          type="button"
                          showSpinner={saveTrip.isPending && saveTrip.variables === 'summary'}
                          disabled={!canSave}
                          onClick={() => saveTrip.mutate('summary')}
                        >
                          {t('travelAndExpenses.trip.saveAndSummary')}
                        </Button>
                      </AsideActionFullWidth>
                    </AsideFormActions>
                  )}
                </Aside>
              </Columns>
            </PageContent>
          )}
        </AnimatedContentLoader>
      </PageScroll>
      {removeMileagePrompt && (
        <Prompt
          isVisible={removeMileagePrompt}
          title={t('travelAndExpenses.trip.removeMileageTitle')}
          description={t('travelAndExpenses.trip.removeMileageDescription')}
          onClose={() => setRemoveMileagePrompt(false)}
          buttons={[
            {
              text: t('general.cancel'),
              action: () => setRemoveMileagePrompt(false)
            },
            {
              text: t('confirm'),
              intent: 'danger',
              showSpinner: removeAllLegs.isPending,
              action: () => removeAllLegs.mutate()
            }
          ]}
        />
      )}
      {removePerDiemPrompt && (
        <Prompt
          isVisible={removePerDiemPrompt}
          title={t('travelAndExpenses.trip.removeDailyAllowanceTitle')}
          description={t('travelAndExpenses.trip.removeDailyAllowanceDescription')}
          onClose={() => setRemovePerDiemPrompt(false)}
          buttons={[
            {
              text: t('general.cancel'),
              action: () => setRemovePerDiemPrompt(false)
            },
            {
              text: t('confirm'),
              intent: 'danger',
              showSpinner: saveAutomatic.isPending,
              action: () => applyRemovePerDiem()
            }
          ]}
        />
      )}
      {removeReceiptsPrompt && (
        <Prompt
          isVisible={removeReceiptsPrompt}
          title={t('travelAndExpenses.trip.removeReceiptsTitle')}
          description={t('travelAndExpenses.trip.removeReceiptsDescription')}
          onClose={() => setRemoveReceiptsPrompt(false)}
          buttons={[
            {
              text: t('general.cancel'),
              action: () => setRemoveReceiptsPrompt(false)
            },
            {
              text: t('confirm'),
              intent: 'danger',
              showSpinner: unlinkAllReceipts.isPending,
              action: () => unlinkAllReceipts.mutate()
            }
          ]}
        />
      )}
      <LegDrawer
        slug={slug}
        draft={draft}
        canEdit={isOwnDraft}
        rates={ratesQuery.data}
        destinations={destinations}
        error={legError}
        isSaving={saveLeg.isPending}
        isDeleting={removeLeg.isPending}
        onClose={() => setDraft(null)}
        onSubmit={next => saveLeg.mutate(next)}
        onDelete={legId => removeLeg.mutate(legId)}
      />
    </SingleColumnLayout>
  )
}

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: ${({ theme }) => theme.spacing.px12}rem;
    top: ${({ theme }) => theme.spacing.px16}rem;
    bottom: ${({ theme }) => theme.spacing.px16}rem;
    border-left: 1px dashed ${({ theme }) => theme.colors.neutralGray};
  }
`

const TimelineItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem 0;
  position: relative;
`

const TimelineButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem 0;
  position: relative;
  border: 0;
  background: transparent;
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  .chevron {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.iconGray};
  }

  &:hover .chevron {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const ReceiptLink = styled(Link)`
  display: block;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  color: inherit !important;
  text-decoration: none !important;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`

const ReceiptMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const ReceiptHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const ReceiptTitle = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const ReceiptAmount = styled.strong`
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const ReceiptTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
`

const TimelineIcon = styled.span<{ $dashed?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ theme }) => theme.iconSize.sm}rem;
  height: ${({ theme }) => theme.iconSize.sm}rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px ${({ $dashed }) => ($dashed ? 'dashed' : 'solid')}
    ${({ theme, $dashed }) => ($dashed ? theme.colors.primary : theme.colors.neutralWhite)};
  color: ${({ theme }) => theme.colors.primary};
  z-index: 1;

  svg {
    width: ${({ theme }) => theme.spacing.px12}rem;
    height: ${({ theme }) => theme.spacing.px12}rem;
  }
`

const LegBody = styled.div`
  flex: 1;
  min-width: 0;
`

const Address = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
`

const Chip = styled.span<{ $highlight?: boolean }>`
  padding: ${({ theme }) => theme.spacing.xxs}rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.pill}px;
  border: 1px solid
    ${({ theme, $highlight }) =>
      $highlight ? `${theme.colors.primary}33` : theme.colors.neutralGray};
  background: ${({ theme, $highlight }) =>
    $highlight ? `${theme.colors.primary}0d` : theme.colors.neutralWhite};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  font-variant-numeric: tabular-nums;
`

const AddLegRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const TotalsBox = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
`
